import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Plus, Pencil, Trash2, Package, Loader2, DollarSign, Hash,
} from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const emptyForm = {
  name: '',
  description: '',
  daily_rate: '',
  pricing_type: 'flat',
  total_quantity: '',
  is_active: true,
  image_url: '',
};

export default function EquipmentManagement() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null); // null => create
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/lanes/equipment/admin');
      setItems(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load equipment');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || '',
      description: item.description || '',
      daily_rate: String(item.daily_rate ?? ''),
      pricing_type: item.pricing_type || 'flat',
      total_quantity: String(item.total_quantity ?? ''),
      is_active: item.is_active !== false,
      image_url: item.image_url || '',
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    const rate = parseFloat(form.daily_rate);
    const qty = parseInt(form.total_quantity, 10);
    if (!form.name.trim()) return toast.error('Name is required');
    if (!Number.isFinite(rate) || rate <= 0) return toast.error('Daily rate must be greater than 0');
    if (!Number.isInteger(qty) || qty <= 0) return toast.error('Total quantity must be a positive integer');

    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      daily_rate: rate,
      pricing_type: form.pricing_type || 'flat',
      total_quantity: qty,
      is_active: form.is_active,
      image_url: form.image_url.trim() || null,
    };

    setSaving(true);
    try {
      if (editing) {
        await api.put(`/lanes/equipment/admin/${editing.id}`, payload);
        toast.success('Equipment updated');
      } else {
        await api.post('/lanes/equipment/admin', payload);
        toast.success('Equipment created');
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    try {
      const res = await api.delete(`/lanes/equipment/admin/${item.id}`);
      toast.success(res.data?.message || 'Equipment removed');
      setConfirmDelete(null);
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="space-y-3">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-2">
              <Package className="h-7 w-7 text-blue-500" />
              Equipment Rentals
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Manage rentable add-ons customers can book alongside a lane reservation.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <Button onClick={openCreate} className="bg-blue-500 hover:bg-blue-600" data-testid="new-equipment-btn">
            <Plus className="h-4 w-4 mr-2" /> New Equipment
          </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base text-slate-700">All Equipment</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-10 flex items-center justify-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-slate-500">
                No equipment yet. Click <span className="font-medium">New Equipment</span> to add your first rentable item.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Total Qty</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id} data-testid={`equipment-row-${item.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{item.name}</p>
                          {item.description && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.description}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center text-slate-800 font-medium">
                          <DollarSign className="h-3.5 w-3.5 text-green-600" />
                          {Number(item.daily_rate).toFixed(2)}
                          <span className="ml-1 text-xs text-slate-500 font-normal">
                            /{(item.pricing_type || 'flat') === 'hourly' ? 'hour' : 'booking'}
                          </span>
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1 text-slate-700">
                          <Hash className="h-3.5 w-3.5 text-slate-400" />
                          {item.total_quantity}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.is_active ? (
                          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500">Disabled</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(item)}
                          data-testid={`edit-equipment-${item.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setConfirmDelete(item)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-equipment-${item.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Equipment' : 'New Equipment'}</DialogTitle>
              <DialogDescription>
                Customers can rent this as an add-on when booking a lane. The rate is a flat per-booking charge.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Bowling Machine"
                  data-testid="equipment-name-input"
                />
              </div>
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  placeholder="Short description shown to customers at checkout"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Rate (USD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.daily_rate}
                    onChange={(e) => setForm({ ...form, daily_rate: e.target.value })}
                    placeholder="25.00"
                    data-testid="equipment-rate-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {form.pricing_type === 'hourly'
                      ? `Charged as ${cs()}${form.daily_rate || '0'}/hour. A 30-min booking pays half.`
                      : 'Flat fee charged once per booking regardless of duration.'}
                  </p>
                </div>
                <div>
                  <Label>Pricing type *</Label>
                  <Select
                    value={form.pricing_type}
                    onValueChange={(v) => setForm({ ...form, pricing_type: v })}
                  >
                    <SelectTrigger data-testid="equipment-pricing-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="flat">Flat (per booking)</SelectItem>
                      <SelectItem value="hourly">Hourly (per hour)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Total quantity *</Label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  value={form.total_quantity}
                  onChange={(e) => setForm({ ...form, total_quantity: e.target.value })}
                  placeholder="2"
                  data-testid="equipment-qty-input"
                />
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <Label className="text-sm">Active</Label>
                  <p className="text-xs text-slate-500">
                    Customers can rent this when booking a lane.
                  </p>
                </div>
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={saving} className="bg-blue-500 hover:bg-blue-600" data-testid="save-equipment-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete equipment?</DialogTitle>
              <DialogDescription>
                {confirmDelete?.name ? (
                  <>
                    You're about to remove <span className="font-medium">{confirmDelete.name}</span>.
                    If any bookings still reference it, it will be disabled instead of deleted.
                  </>
                ) : null}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button
                onClick={() => remove(confirmDelete)}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-delete-equipment"
              >
                Remove
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
